import type { AutocompleteInputChangeReason } from "@mui/material/Autocomplete";
import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useSearchInsightsQuery } from "~/feature/insights/insightsApi";
import { MIN_QUERY_LEN } from "~/feature/search/constants";
import { useLazyGetSearchEntitiesQuery,useLazyGetSearchEventsQuery } from "~/feature/search/searchApi";
import type {
  ISearchEntity,
  ISearchInsightsSummary,
} from "~/feature/search/type";
import { findMatchingEntity } from "~/feature/search/utils/findMatchingEntity";
import { useDebouncedValue } from "~/hooks/useDebouncedValue";
import { EventSortByEnum, type IAggregatedEvent } from "~/types/event";
import { SortOrderEnum } from "~/types/sort";
import { handleError } from "~/utils/handleError";

import { useSearchPageCleanup } from "./useSearchPageCleanup";

interface IUseAppSearchResult {
  inputValue: string;
  query: string;
  options: IAggregatedEvent[];
  entity: ISearchEntity | null;
  insights: ISearchInsightsSummary | null;
  loading: boolean;
  noOptionsText: string;
  handleInputChange: (
    event: SyntheticEvent,
    nextValue: string,
    reason: AutocompleteInputChangeReason
  ) => void;
}

export const useAppSearch = (): IUseAppSearchResult => {
  const [inputValue, setInputValue] = useState("");

  const clearSearch = useCallback(() => {
    setInputValue("");
  }, []);

  useSearchPageCleanup({ inputValue, clearSearch });

  const query = inputValue.trim();
  const debouncedQuery = useDebouncedValue(query);

  const [getSearchResults, { data, isFetching }] =
    useLazyGetSearchEventsQuery();

  const [getSearchEntities, { data: entitiesData }] =
    useLazyGetSearchEntitiesQuery();

  const { currentData: insightsData, isFetching: isInsightsFetching } =
    useSearchInsightsQuery(
      { query: debouncedQuery },
      { skip: debouncedQuery.length < MIN_QUERY_LEN }
    );

  useEffect(() => {
    if (debouncedQuery.length < MIN_QUERY_LEN) return;

    const req = getSearchResults({
      query: debouncedQuery,
      sortBy: EventSortByEnum.VOLUME,
      sortOrder: SortOrderEnum.DESC,
    });
    req.unwrap().catch(handleError);

    const entityReq = getSearchEntities({ query: debouncedQuery });
    entityReq.unwrap().catch(handleError);

    return () => {
      req.abort();
      entityReq.abort();
    };
  }, [debouncedQuery, getSearchResults, getSearchEntities]);

  const waitingDebounce =
    query.length >= MIN_QUERY_LEN && query !== debouncedQuery;
  const loading = isFetching || waitingDebounce;

  const options = useMemo<IAggregatedEvent[]>(() => {
    if (query.length < MIN_QUERY_LEN) return [];
    if (query !== debouncedQuery) return [];
    return data?.data ?? [];
  }, [data, query, debouncedQuery]);

  const entity = useMemo<ISearchEntity | null>(() => {
    if (query.length < MIN_QUERY_LEN) return null;
    if (query !== debouncedQuery) return null;
    if (!entitiesData?.length) return null;

    return findMatchingEntity(entitiesData, query);
  }, [entitiesData, query, debouncedQuery]);

  const insights = useMemo<ISearchInsightsSummary | null>(() => {
    const total = insightsData?.length ?? 0;
    if (query !== debouncedQuery || isInsightsFetching || total === 0) {
      return null;
    }

    return { query, total };
  }, [insightsData, query, debouncedQuery, isInsightsFetching]);

  const noOptionsText =
    query.length < MIN_QUERY_LEN
      ? `Type at least ${MIN_QUERY_LEN} characters`
      : "No results";

  const handleInputChange = useCallback(
    (
      _: SyntheticEvent,
      nextValue: string,
      reason: AutocompleteInputChangeReason
    ) => {
      if (reason === "reset") return;

      setInputValue(nextValue);
    },
    []
  );

  return {
    inputValue,
    query,
    options,
    entity,
    insights,
    loading,
    noOptionsText,
    handleInputChange,
  };
};
